import React, { useEffect, useState, useContext } from 'react'
import ReactModal from 'react-modal'
import { FormattedMessage } from 'react-intl'
import PropTypes from 'prop-types'
import CloseIcon from 'assets/icons/close-icon'
import { CloseButton, CancelButton, ModalAutoSaveWrapper, ModalLabelContainer, StyledCloseButton } from './style'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import AutoSaveIcon from 'features/AutoSaveIcon'
import { useRequestQueue } from 'hooks/useRequestQueue'
import InfoHeader from 'components/InfoHeader'
import Button from 'components/Button'
import { requestGetSiteSpecie } from 'api/siteSpecie'
import NonQualifyWarningModal from './NonQualifyWarningModal'
import { SiteContext } from 'providers/site'

const customStyles = {
  content: {
    padding: '0',
    borderRadius: '8px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    pointerEvents: 'auto',
    transform: 'translate(-50%, -50%)'
  },
  overlay: {
    backgroundColor: 'transparent',
    pointerEvents: 'none',
    zIndex: 1000
  }
}

const customStylesComments = {
  content: {
    padding: '0',
    top: '0',
    left: 'auto',
    right: '0',
    bottom: 'auto',
    marginLeft: '-50%',
    pointerEvents: 'auto'
  },
  overlay: {
    backgroundColor: 'transparent',
    pointerEvents: 'none',
    zIndex: 1000
  }
}

const tooltipXOffset = 0
const tooltipYOffset = 16
const BIG_MODAL_STYLE = { ...customStyles, content: { ...customStyles.content, height: '90vh', overflowY: 'scroll' } }

const getModalStyle = ({ bigModal, sideModal }) => {
  if (bigModal) {
    return BIG_MODAL_STYLE
  }

  if (sideModal) {
    return customStylesComments
  }

  return customStyles
}

const getTitleValues = ({ titleValue, assessmentValue }) => {
  if (titleValue) {
    return { commentNumber: <>{`(${titleValue})`}</> }
  }

  if (assessmentValue) {
    return { assessmentsNumber: <>{`(${assessmentValue})`}</> }
  }

  return undefined
}

const Modal = ({
  children,
  isOpen,
  toggleModal,
  toggleModal1,
  modalTitleId,
  infoTooltip,
  zIndex,
  infoHeader,
  paddingBottom,
  paddingTop,
  marginLeft,
  justifyContent,
  label,
  width = '416px',
  bottomBorder = true,
  padding = '32px',
  height = '',
  bigModal = false,
  sideModal = false,
  titleValue,
  assessmentValue,
  cancelTitleButton = 'cancel',
  autoSaveIcon = false,
  isDraft = false,
  cancelHandler,
  bigModalTittle = cancelHandler ? 'reassess' : 'finishEditing',
  speciesToReassess,
  selectedReasons
}) => {
  const isLoading = useRequestQueue()
  const [taxonTreatment, setTaxonTreatment] = useState('')
  const [showWarning, setShowWarning] = useState(false)

  const { triggerReasonId } = useContext(SiteContext)

  useEffect(() => {
    if (speciesToReassess) {
      requestGetSiteSpecie(speciesToReassess.id, speciesToReassess.SiteHistoryId, speciesToReassess.historyId).then(
        (data) => {
          setTaxonTreatment(data.taxonTreatment)
        }
      )
    }
  }, [speciesToReassess])

  const handleReassessClicked = () => {
    const warningCategories = ['A-NR', 'Q-NR', 'S-NR', 'U-NR', 'U-UR', 'A-UR']
    if (warningCategories.includes(taxonTreatment) && triggerReasonId.includes(1)) {
      setShowWarning(true)
    } else {
      const toggleFn = toggleModal1 || toggleModal
      toggleFn(true)
    }
  }

  const handleFinishEditingClicked = () => {
    if (toggleModal1) {
      return toggleModal1(false)
    }
    return toggleModal(false)
  }

  return (
    <div data-testid="test-react-modal">
      <ReactModal
        isOpen={isOpen}
        style={getModalStyle({ bigModal, sideModal })}
        shouldCloseOnOverlayClick={true}
      >
        <div style={{ width }}>
          <div style={{ position: 'relative' }}>
            <div style={bigModal ? { position: 'sticky', top: '0px', zIndex: '1000', background: 'white' } : {}}>
              {autoSaveIcon && (
                <ModalAutoSaveWrapper>
                  <AutoSaveIcon loading={isLoading} time={'saved'} />
                </ModalAutoSaveWrapper>
              )}
              <CloseButton
                data-testid="test-close-button-modal"
                data-test="close-button"
                bigModal={bigModal}
                onClick={cancelHandler ? handleReassessClicked : handleFinishEditingClicked}
              >
                {bigModal && isDraft ? (
                  <StyledCloseButton>
                    <FormattedMessage id={bigModalTittle} />
                  </StyledCloseButton>
                ) : (
                  <CloseIcon />
                )}
              </CloseButton>
              {cancelHandler && (
                <CancelButton>
                  <Button type="secondary" height="40px" marginTop="0" onClick={cancelHandler}>
                    <FormattedMessage id={cancelTitleButton} />
                  </Button>
                </CancelButton>
              )}

              <ModalLabelContainer
                data-test="modal-label"
                bottomBorder={bottomBorder}
                paddingBottom={paddingBottom}
                paddingTop={paddingTop}
                paddingRight={'32px'}
                marginLeft={marginLeft}
                justifyContent={justifyContent}
              >
                <FormattedMessage id={modalTitleId} values={getTitleValues({ titleValue, assessmentValue })} />
                {infoHeader && <InfoHeader text={infoHeader}></InfoHeader>}
                {infoTooltip && (
                  <Tooltip
                    style={{ zIndex: 1000 }}
                    zIndex={zIndex}
                    placement="bottom"
                    trigger={['hover']}
                    align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                    overlay={{ label: label, infoTooltip: infoTooltip }}
                  >
                    <div>
                      <InfoIcon />
                    </div>
                  </Tooltip>
                )}
              </ModalLabelContainer>
            </div>
            <div style={{ padding: padding, height: height }}>{children}</div>
          </div>
        </div>
      </ReactModal>
      {showWarning && <NonQualifyWarningModal isOpen={showWarning} toggleModal={setShowWarning} />}
    </div>
  )
}

export default Modal

Modal.propTypes = {
  children: PropTypes.node,
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
  toggleModal1: PropTypes.func,
  modalTitleId: PropTypes.string,
  infoTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  zIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  infoHeader: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  paddingBottom: PropTypes.string,
  paddingTop: PropTypes.string,
  marginLeft: PropTypes.string,
  justifyContent: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  width: PropTypes.string,
  bottomBorder: PropTypes.bool,
  padding: PropTypes.string,
  height: PropTypes.string,
  bigModal: PropTypes.bool,
  sideModal: PropTypes.bool,
  titleValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assessmentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cancelTitleButton: PropTypes.string,
  autoSaveIcon: PropTypes.bool,
  isDraft: PropTypes.bool,
  cancelHandler: PropTypes.func,
  bigModalTittle: PropTypes.string,
  speciesToReassess: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    SiteHistoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    historyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  selectedReasons: PropTypes.arrayOf(PropTypes.any)
}
